import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { Repositories } from '@/infra/repositories';
import { type GetCartSummaryProps } from '@/infra/repositories/checkout/cart/get-cart-summary';
import { type Response as CartSummaryResponse } from '@/infra/services/checkout/cart/get-cart-summary';
import { useGlobalStore } from '@/presenters/store/global';

import { QUERY_KEY } from './constants';

type QueryOptions<TData = CartSummaryResponse> = Omit<
  UseQueryOptions<CartSummaryResponse, Error, TData>,
  'queryKey' | 'queryFn'
>;

interface UseCardSummaryProps
  extends Pick<
    GetCartSummaryProps,
    'installments' | 'paymentMethod' | 'vouchers'
  > {}

export function useCartSummary<TData = CartSummaryResponse>(params?: {
  props?: Readonly<UseCardSummaryProps>;
  options?: QueryOptions<TData>;
}) {
  const cartId = useGlobalStore((store) => store.session?.cartId);

  const vouchers = params?.props?.vouchers ?? [];

  const query = useQuery<CartSummaryResponse, Error, TData>({
    queryKey: [
      QUERY_KEY,
      cartId,
      params?.props?.paymentMethod,
      params?.props?.installments,
      vouchers,
    ],
    queryFn: async () => {
      if (!cartId) {
        throw new Error('Cart ID not found');
      }

      return await Repositories.Checkout.Cart.getCartSummary({
        cartId,
        paymentMethod: params?.props?.paymentMethod,
        installments: params?.props?.installments,
        vouchers,
      });
    },
    enabled: !!cartId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
    ...params?.options,
  });

  return { ...query, isLoading: query.isLoading, error: query.error };
}
